import { Outlet, ScrollRestoration, useMatches, useParams } from 'react-router-dom';
import axios from 'axios';
import { useEffect } from 'react';

import { LoadingPage } from '@foradevelopment/fora-ui';
import { ThunkRequestStatus } from '@fora-monorepo/webapp-types';

import { EmployerMyPagesErrorPage } from './components';
import { envConfig } from './config';
import { useAppDispatch, useAppSelector } from './redux/hooks';
import { getAdministrableEmployers, getEmployer } from './redux/employer/employerThunks';
import { Authentication } from './auth/components/Authentication';
import { AuthenticationForaKS } from './auth/components/AuthenticationForaKS';
import { getPagesContent } from './redux/content/contentThunks';
import { getLastMatchingTitle } from './common/utils';
import { getUnderMaintenance } from './redux/error/errorThunks';
import { getSettings } from './redux/fora-global/foraGlobalThunks';
import { useTrackPageViews } from './analytics/matomo/useTrackPageViews';
import { useTypedTranslation } from './i18n/i18n';

axios.defaults.headers.common['Ocp-Apim-Subscription-Key'] = envConfig.apimSubscriptionKey;

export const App = () => {
  useTrackPageViews();
  const dispatch = useAppDispatch();
  const { partnerId } = useParams();
  const matches = useMatches();
  const { t } = useTypedTranslation();

  const loadingEmployerInfo = useAppSelector(state => state.employer.loadingEmployerInfo);
  const loadingSettings = useAppSelector(state => state.foraGlobal.loadingSettings);
  const { authenticated, loggedOut, user } = useAppSelector(state => state.auth);
  const thunkError = useAppSelector(state => state.error.thunkError);
  const loadingUnderMaintenance = useAppSelector(state => state.error.loadingUnderMaintenance);
  const underMaintenance = useAppSelector(state => state.error.underMaintenance);

  useEffect(() => {
    if (authenticated) {
      // TODO: Hämta igen när man går in på multi-rapportörsida.
      dispatch(getAdministrableEmployers(user?.role));
      if (user?.selectedPartnerNumberOrganization) {
        dispatch(getEmployer());
      }
      dispatch(getPagesContent());
      dispatch(getSettings());
    } else {
      dispatch(getUnderMaintenance());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, dispatch]);

  useEffect(() => {
    document.title = getLastMatchingTitle(matches, t).title;
  }, [matches, t]);

  const authenticate = () => {
    if (loggedOut) {
      return <EmployerMyPagesErrorPage type={401} />;
    } else if (partnerId && !authenticated) {
      // Send user to KS login
      return <AuthenticationForaKS />;
    } else if (!authenticated) {
      // Authenticate user or refresh token
      return <Authentication />;
    } else {
      // TODO: måste nog göra något åt loadingEmployerInfo så inte hela sidan laddas om (banner fösvinner) när man kör change employer.
      // Kanske lägga layouten på denna nivå, eller flytta ner loadingEmployerInfo if-satsen till Layout-komponenten
      if (
        (loadingEmployerInfo === ThunkRequestStatus.Fulfilled || !loadingEmployerInfo) &&
        loadingSettings === ThunkRequestStatus.Fulfilled
      ) {
        return <Outlet />;
      } else if (loadingEmployerInfo === ThunkRequestStatus.Pending || loadingSettings === ThunkRequestStatus.Pending) {
        return <LoadingPage animation="spinner" />;
      }
      return <EmployerMyPagesErrorPage technicalError={thunkError?.message} type={thunkError?.code} />;
    }
  };

  if (underMaintenance) {
    return <EmployerMyPagesErrorPage technicalError={thunkError?.message} type={503} />;
  }

  return (
    <>
      <ScrollRestoration />
      {loadingUnderMaintenance === ThunkRequestStatus.Fulfilled && authenticate()}
    </>
  );
};
